import { useTranslation } from 'react-i18next';

// Sukhbaatar Square coordinates
const SUKHBAATAR_SQUARE = { lat: 47.9192, lng: 106.9177 };

// Calculate distance between two coordinates in km
const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Convert km to walking time (average walking speed ~5km/h)
const getWalkingTime = (km: number): string => {
  const minutes = Math.round((km / 5) * 60);
  if (minutes < 1) return '1 min';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainingMins = minutes % 60;
  if (remainingMins === 0) return `${hours}h`;
  return `${hours}h ${remainingMins}m`;
};

interface Place {
  id: number;
  nameMn: string;
  nameEn: string;
  category: string;
  mood: string[];
  budget: number;
  rating: number;
  views: number;
  isOpen: boolean;
  verified: string;
  tip: string;
  image: string;
  timeOfDay: string[];
  lat: number;
  lng: number;
  distance?: number;
}

interface PlaceCardProps {
  place: Place;
  onClick: () => void;
  showDistance?: boolean;
}

export default function PlaceCard({ place, onClick, showDistance }: PlaceCardProps) {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';

  const formatDistance = (km: number) => {
    if (km < 1) {
      return `${Math.round(km * 1000)}m`;
    }
    return `${km.toFixed(1)}km`;
  };

  // Calculate distance from Sukhbaatar Square
  const distanceFromSquare = calculateDistance(
    SUKHBAATAR_SQUARE.lat,
    SUKHBAATAR_SQUARE.lng,
    place.lat,
    place.lng
  );
  const walkingTime = getWalkingTime(distanceFromSquare);

  return (
    <article 
      onClick={onClick}
      className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer group"
    >
      {/* Image - Square aspect ratio on mobile, 4:5 on desktop */}
      <div className="relative w-full aspect-square md:aspect-[4/5] overflow-hidden">
        <img
          src={place.image}
          alt={isEnglish ? place.nameEn : place.nameMn}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute top-2 left-2 flex gap-1.5">
          <span className={`px-1.5 py-0.5 md:px-2 md:py-1 rounded-full text-[10px] md:text-xs font-medium ${
            place.isOpen ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
          }`}>
            {place.isOpen ? t('placeCard.open') : t('placeCard.closed')}
          </span>
        </div>
        {showDistance && place.distance !== undefined && (
          <div className="absolute top-2 right-2 bg-teal-500 text-white px-1.5 py-0.5 md:px-2 md:py-1 rounded-full text-[10px] md:text-xs font-medium flex items-center gap-0.5">
            <i className="ri-navigation-line text-xs"></i>
            {formatDistance(place.distance)}
          </div>
        )}
        <div className="absolute bottom-2 right-2 flex items-center gap-0.5 bg-black/60 backdrop-blur-sm px-1.5 py-0.5 md:px-2 md:py-1 rounded-full">
          <i className="ri-star-fill text-yellow-400 text-xs"></i>
          <span className="text-white text-xs font-medium">{place.rating}</span>
        </div>
      </div>

      {/* Content - Compact on mobile */}
      <div className="p-2 md:p-4">
        <div className="flex items-start justify-between mb-1 md:mb-2">
          <h3 className="font-bold text-gray-900 text-sm md:text-lg leading-tight line-clamp-2 flex-1 pr-2">
            {isEnglish ? place.nameEn : place.nameMn}
          </h3>
          <span className="text-teal-600 font-medium text-xs md:text-sm flex-shrink-0">
            {'$'.repeat(place.budget)}
            <span className="text-gray-300">{'$'.repeat(3 - place.budget)}</span>
          </span>
        </div>

        {/* Tip - Hidden on mobile, shown on desktop */}
        <p className="hidden md:block text-gray-500 text-sm mb-3 line-clamp-2 italic">
          "{place.tip}"
        </p>

        <div className="flex items-center justify-between text-[10px] md:text-xs text-gray-400">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="flex items-center gap-0.5 md:gap-1">
              <i className="ri-eye-line text-xs"></i>
              <span className="hidden md:inline">{place.views.toLocaleString()} {t('placeCard.views')}</span>
              <span className="md:hidden">{place.views > 999 ? `${(place.views / 1000).toFixed(1)}k` : place.views}</span>
            </div>
            <div className="flex items-center gap-0.5 md:gap-1 text-amber-600">
              <i className="ri-walk-line text-xs"></i>
              <span>{walkingTime}</span>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-1 text-teal-600">
            <i className="ri-verified-badge-fill"></i>
            <span>{t('placeCard.verified')}</span>
          </div>
        </div>
      </div>
    </article>
  );
}